import { VehicleAccidentState, VehicleValuationRimType, VehicleValuationType } from "@/api-schema-enums";

export const VEHICLE_ACCIDENT_STATE_VALUES = [
  VehicleAccidentState.UNKNOWN,
  VehicleAccidentState.AccidentFree,
  VehicleAccidentState.ExistingDamage,
  VehicleAccidentState.RepairedDamage,
] as const;

export const VEHICLE_VALUATION_RIM_TYPE_VALUES = [
  VehicleValuationRimType.OriginalRims,
  VehicleValuationRimType.NonOriginalRims,
] as const;

export const VEHICLE_BOOLEAN_SELECT_VALUES = ["true", "false"] as const;

export type VehicleBooleanSelectValue = (typeof VEHICLE_BOOLEAN_SELECT_VALUES)[number];
type TranslatedOption<T extends string> = { value: T; label: string };

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const VEHICLE_ACCIDENT_STATE_TRANSLATION_KEYS = {
  [VehicleAccidentState.AccidentFree]: "masterdata:VehicleAccidentStateAccidentFree",
  [VehicleAccidentState.ExistingDamage]: "masterdata:VehicleAccidentStateExistingDamage",
  [VehicleAccidentState.RepairedDamage]: "masterdata:VehicleAccidentStateRepairedDamage",
  [VehicleAccidentState.UNKNOWN]: "masterdata:VehicleAccidentStateUnknown",
} as const satisfies Record<VehicleAccidentState, string>;

const VEHICLE_VALUATION_RIM_TYPE_TRANSLATION_KEYS = {
  [VehicleValuationRimType.OriginalRims]: "fab:RimTypeOriginalRims",
  [VehicleValuationRimType.NonOriginalRims]: "fab:RimTypeNonOriginalRims",
} as const satisfies Record<VehicleValuationRimType, string>;

function getTranslatedOptions<T extends string>(
  values: readonly T[],
  getLabel: (value: T) => string | undefined,
): TranslatedOption<T>[] {
  return values.map((value) => ({
    value,
    label: getLabel(value) ?? value,
  }));
}

export function isVehicleAccidentState(value: string): value is VehicleAccidentState {
  return VEHICLE_ACCIDENT_STATE_VALUES.includes(value as VehicleAccidentState);
}

export function isVehicleBooleanSelectValue(value: string): value is VehicleBooleanSelectValue {
  return VEHICLE_BOOLEAN_SELECT_VALUES.includes(value as VehicleBooleanSelectValue);
}

export function isVehicleValuationRimType(value: string): value is VehicleValuationRimType {
  return VEHICLE_VALUATION_RIM_TYPE_VALUES.includes(value as VehicleValuationRimType);
}

export function getVehicleAccidentStateLabel(
  value: VehicleAccidentState | null | undefined,
  t: TranslateFn,
): string | undefined {
  if (!value) {
    return undefined;
  }

  return t(VEHICLE_ACCIDENT_STATE_TRANSLATION_KEYS[value]);
}

export function shouldShowVehicleAccidentDetails(value: VehicleAccidentState | null | undefined): boolean {
  return value === VehicleAccidentState.ExistingDamage || value === VehicleAccidentState.RepairedDamage;
}

export function getVehicleBooleanLabel(value: boolean | null | undefined, t: TranslateFn): string | undefined {
  if (value == null) {
    return undefined;
  }

  return value ? t("common:Yes") : t("common:No");
}

export function getVehicleValuationRimTypeLabel(
  value: VehicleValuationRimType | null | undefined,
  t: TranslateFn,
): string | undefined {
  if (!value) {
    return undefined;
  }

  return t(VEHICLE_VALUATION_RIM_TYPE_TRANSLATION_KEYS[value]);
}

export function getVehicleAccidentStateOptions(t: TranslateFn): Array<{ value: VehicleAccidentState; label: string }> {
  return getTranslatedOptions(VEHICLE_ACCIDENT_STATE_VALUES, (value) => getVehicleAccidentStateLabel(value, t));
}

export function getVehicleValuationRimTypeOptions(
  t: TranslateFn,
): Array<{ value: VehicleValuationRimType; label: string }> {
  return getTranslatedOptions(VEHICLE_VALUATION_RIM_TYPE_VALUES, (value) => getVehicleValuationRimTypeLabel(value, t));
}

export function getVehicleBooleanSelectOptions(
  t: TranslateFn,
): Array<{ value: VehicleBooleanSelectValue; label: string }> {
  return [
    { value: "true", label: t("common:Yes") },
    { value: "false", label: t("common:No") },
  ];
}

export function getVehicleVatDeductibleSelectOptions(
  t: TranslateFn,
): Array<{ value: VehicleBooleanSelectValue; label: string }> {
  return [
    { value: "true", label: t("masterdata:VatDeductibleYesOption") },
    { value: "false", label: t("masterdata:VatDeductibleNoOption") },
  ];
}

export function toVehicleBooleanSelectValue(value: boolean | null | undefined): VehicleBooleanSelectValue | "" {
  if (value == null) {
    return "";
  }

  return value ? "true" : "false";
}

export function parseVehicleBooleanSelectValue(value: VehicleBooleanSelectValue): boolean {
  return value === "true";
}

export const VEHICLE_VALUATION_TYPE_VALUES = [
  VehicleValuationType.Default,
  VehicleValuationType.LeasingBuyout,
  VehicleValuationType.WholesaleBuyer,
] as const;

export const VEHICLE_VALUATION_CREATE_TYPE_VALUES = [
  VehicleValuationType.Default,
  VehicleValuationType.LeasingBuyout,
] as const;

export const VEHICLE_VALUATION_CONFIRM_TYPE_VALUES = [
  VehicleValuationType.Default,
  VehicleValuationType.WholesaleBuyer,
] as const;

const VEHICLE_VALUATION_TYPE_TRANSLATION_KEYS = {
  [VehicleValuationType.Default]: "fab:VehicleValuationTypeDefault",
  [VehicleValuationType.LeasingBuyout]: "fab:VehicleValuationTypeLeasingBuyout",
  [VehicleValuationType.WholesaleBuyer]: "fab:VehicleValuationTypeWholesaleBuyer",
} as const satisfies Record<VehicleValuationType, string>;

const VEHICLE_VALUATION_TYPE_HINT_TRANSLATION_KEYS = {
  [VehicleValuationType.Default]: "fab:VehicleValuationTypeDefaultHint",
  [VehicleValuationType.LeasingBuyout]: "fab:VehicleValuationTypeLeasingBuyoutHint",
  [VehicleValuationType.WholesaleBuyer]: "fab:VehicleValuationTypeWholesaleBuyerHint",
} as const satisfies Record<VehicleValuationType, string>;

const VEHICLE_VALUATION_TYPE_SUMMARY_TRANSLATION_KEYS = {
  [VehicleValuationType.Default]: "fab:VehicleValuationTypeDefaultSummary",
  [VehicleValuationType.LeasingBuyout]: "fab:VehicleValuationTypeLeasingBuyoutSummary",
  [VehicleValuationType.WholesaleBuyer]: "fab:VehicleValuationTypeWholesaleBuyerSummary",
} as const satisfies Record<VehicleValuationType, string>;

export function getVehicleValuationTypeLabel(
  value: VehicleValuationType | null | undefined,
  t: TranslateFn,
): string | undefined {
  if (!value) {
    return undefined;
  }

  return t(VEHICLE_VALUATION_TYPE_TRANSLATION_KEYS[value]);
}

export function getVehicleValuationTypeHint(
  value: VehicleValuationType | null | undefined,
  t: TranslateFn,
): string | undefined {
  if (!value) {
    return undefined;
  }

  return t(VEHICLE_VALUATION_TYPE_HINT_TRANSLATION_KEYS[value]);
}

export function getVehicleValuationTypeSummary(
  value: VehicleValuationType | null | undefined,
  t: TranslateFn,
): string | undefined {
  if (!value) {
    return undefined;
  }

  return t(VEHICLE_VALUATION_TYPE_SUMMARY_TRANSLATION_KEYS[value]);
}

export function getVehicleValuationTypeOptions(
  t: TranslateFn,
  values: readonly VehicleValuationType[] = VEHICLE_VALUATION_TYPE_VALUES,
): Array<{ value: VehicleValuationType; label: string }> {
  return values.map((value) => ({
    value,
    label: getVehicleValuationTypeLabel(value, t) ?? value,
  }));
}
